export const CUSTOM_ROLE_RANK = 4;

export const DEFAULT_PROJECT_ROLES = [
  { id: 1, name: "PROJECT_MANAGER", frontendName: "Manager", rank: 1 },
  { id: 2, name: "PROJECT_EDITOR", frontendName: "Editor", rank: 2 },
  { id: 3, name: "PROJECT_PARTICIPANT", frontendName: "Participant", rank: 3 },
] as const;

export const DEFAULT_PORTAL_ROLES = [
  { id: 4, name: "PORTAL_SUPER_ADMIN", frontendName: "Super Admin", rank: 1 },
  { id: 5, name: "PORTAL_ADMIN", frontendName: "Admin", rank: 2 },
  { id: 6, name: "PORTAL_MEMBER", frontendName: "Member", rank: 3 },
] as const;

export type RoleFrontendName =
  (typeof DEFAULT_PROJECT_ROLES)[number]["frontendName"] | (typeof DEFAULT_PORTAL_ROLES)[number]["frontendName"];

type RoleTable = readonly {
  readonly id: number;
  readonly name: string;
  readonly frontendName: RoleFrontendName;
}[];

const keyByName = <T extends RoleTable>(roles: T) =>
  Object.fromEntries(roles.map((role) => [role.name, role])) as {
    [Name in T[number]["name"]]: Extract<T[number], { name: Name }>;
  };

const rankByName = <T extends readonly { readonly name: string; readonly rank: number }[]>(roles: T) =>
  Object.fromEntries(roles.map((role) => [role.name, role.rank])) as { [Name in T[number]["name"]]: number };

export type RoleNameTranslator = (frontendName: RoleFrontendName) => string;

const keepEnglishName: RoleNameTranslator = (frontendName) => frontendName;

const translateNames = <T extends RoleTable>(roles: T, t: RoleNameTranslator) =>
  roles.map((role) => ({ ...role, frontendName: t(role.frontendName) }));

export type DefaultProjectRole = (typeof DEFAULT_PROJECT_ROLES)[number];
export type DefaultProjectRoleName = DefaultProjectRole["name"];
export const defaultProjectRoleByName = keyByName(DEFAULT_PROJECT_ROLES);
export const defaultProjectRoleRanks = rankByName(DEFAULT_PROJECT_ROLES);
export const defaultProjectRoleIds = DEFAULT_PROJECT_ROLES.map((role) => String(role.id));

export const CREATOR_PROJECT_ROLE = defaultProjectRoleByName.PROJECT_MANAGER;

export type DefaultPortalRole = (typeof DEFAULT_PORTAL_ROLES)[number];
export type DefaultPortalRoleName = DefaultPortalRole["name"];
export const defaultPortalRoleByName = keyByName(DEFAULT_PORTAL_ROLES);
export const defaultPortalRoleRanks = rankByName(DEFAULT_PORTAL_ROLES);

export const SUPER_ADMIN_PORTAL_ROLE = defaultPortalRoleByName.PORTAL_SUPER_ADMIN;

export type PortalRoleRanks = Record<DefaultPortalRoleName | "CUSTOM_PORTAL_ROLE", number>;
export type ProjectRoleRanks = Record<DefaultProjectRoleName | "CUSTOM_PROJECT_ROLE", number>;

export const getDefaultProjectRoles = (t: RoleNameTranslator = keepEnglishName) =>
  translateNames(DEFAULT_PROJECT_ROLES, t);

export const getDefaultPortalRoles = (t: RoleNameTranslator = keepEnglishName) =>
  translateNames(DEFAULT_PORTAL_ROLES, t);
